/**
 * Redux slice for org-scoped OncoEMR user roster (assignees / message recipients).
 * @module oncoemrUsersSlice
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchOncoemrUsersSearch } from "../../api/getCall/fetchOncoemrUsersSearch";
import { OncoemrUser } from "../../data-model/oncoemrUsersModel";
import { AppDispatch, RootState } from "../store/store";

/** Cached OncoEMR users for the active worklist org. */
export interface OncoemrUsersState {
  /** Org id that `users` was loaded for; null before first fetch. */
  orgId: string | null;
  users: OncoemrUser[];
  loading: boolean;
  error: string | null;
}

const initialState: OncoemrUsersState = {
  orgId: null,
  users: [],
  loading: false,
  error: null,
};

export const oncoemrUsersSlice = createSlice({
  name: "oncoemrUsers",
  initialState,
  reducers: {
    setOncoemrUsersLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setOncoemrUsers: (
      state,
      action: PayloadAction<{ orgId: string; users: OncoemrUser[] }>,
    ) => {
      state.orgId = action.payload.orgId;
      state.users = action.payload.users;
      state.loading = false;
      state.error = null;
    },
    setOncoemrUsersError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetOncoemrUsers: (state) => {
      state.orgId = null;
      state.users = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setOncoemrUsersLoading,
  setOncoemrUsers,
  setOncoemrUsersError,
  resetOncoemrUsers,
} = oncoemrUsersSlice.actions;

/**
 * Loads the full enabled user roster for an org (no `q` or `limit`).
 * Skips when the same org is already cached unless `force` is true.
 *
 * @param orgId - Organization scope for the query.
 * @param force - When true, refetches even if data for this org is already present.
 */
export const fetchOncoemrUsersForOrg =
  (orgId: string, force = false) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { oncoemrUsers } = getState();

    if (!orgId) {
      return;
    }

    if (oncoemrUsers.loading) {
      return;
    }

    const cacheHit =
      !force &&
      oncoemrUsers.orgId === orgId &&
      oncoemrUsers.users.length > 0 &&
      !oncoemrUsers.error;

    if (cacheHit) {
      return;
    }

    if (oncoemrUsers.orgId !== orgId) {
      dispatch(resetOncoemrUsers());
    }

    dispatch(setOncoemrUsersLoading(true));

    try {
      const users = await fetchOncoemrUsersSearch({ org_id: orgId });
      dispatch(setOncoemrUsers({ orgId, users }));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch OncoEMR users";
      dispatch(setOncoemrUsersError(message));
    }
  };

export default oncoemrUsersSlice.reducer;
